import { useEffect, useState } from 'react';
import { CONFIG } from 'config';

interface Props {
  url: string;
  id: string;
  type: string;
  unmountCallback?: () => void;
}

export enum ScriptStatus {
  Loading = 'loading',
  Ready = 'ready',
  Error = 'error',
}

export const useScript = ({ url, id, type, unmountCallback }: Props) => {
  // Keep track of script status ("loading", "ready", "error")
  const [status, setStatus] = useState<ScriptStatus>(ScriptStatus.Loading);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = url;
    script.async = true;
    script.id = id;
    script.type = type;
    script.defer = true;
    document.body.appendChild(script);

    // Script event handler to update status in state
    const setStateFromEvent = (event: Event) => {
      setStatus(
        event.type === 'load' ? ScriptStatus.Ready : ScriptStatus.Error,
      );
    };
    script.addEventListener('load', setStateFromEvent);
    script.addEventListener('error', setStateFromEvent);
    return () => {
      script.removeEventListener('load', setStateFromEvent);
      script.removeEventListener('error', setStateFromEvent);
      document.body.removeChild(script);
      if (unmountCallback) {
        unmountCallback();
      }
    };
  }, []);

  return status;
};

export const useHubspotScript = () =>
  useScript({
    url: `//js.hs-scripts.com/${CONFIG.hubspotAccountId}.js`,
    id: 'hs-script-loader',
    type: 'text/javascript',
  });

export const useChurnZero = () =>
  useScript({
    url: `https://${CONFIG.churnDomain}.us2app.churnzero.net/churnzero.js`,
    id: 'ChurnZero-script-loader',
    type: 'text/javascript',
  });

export const useHurdlrScript = () =>
  useScript({
    url: '//cdn.hurdlr.com/sdk/stable/hurdlr.js',
    id: 'hurdlr-script-loader',
    type: 'text/javascript',
  });

export const useUserPilotScript = () => {
  (window as any).userpilotSettings = {
    token: CONFIG.userpilot.apiKey,
  };
  useScript({
    url: `https://js.userpilot.io/sdk/latest.js`,
    id: 'userpilot-script-loader',
    type: 'text/javascript',
  });
};

export const useWorkNetScript = (
  accountId: string,
  hubspotContactId: string,
) => {
  // eslint-disable-next-line no-underscore-dangle
  (window as any).__WorknetWidget = {
    buttonSide: 'right',
    isDrawer: true,
    headerText: 'Formations AI',
    groupingId: accountId,
    customContext: {
      account_id: accountId,
      hubspot_contact_id: hubspotContactId,
    },
    styles: {
      primary: '#587AFF',
      secondary: '#FFFFFF',
      width: '600px',
      zIndex: 1000,
      display: 'none',
    },
  };

  const widgetId =
    CONFIG.environment === 'production'
      ? 'dfc05c3ddb6cc8b5e0eb16c6af3e80b6e9fac26765d65e4b87cc9e60ed2f861efed5ee93cdec6cff71bafc841cbf9da4280b8578846f315fe5ab1b8ae0a85f04a91973c5c5fa42c58336f8739a51a36c'
      : 'dfc05c3ddb6cc8b5e0eb16c6af3e80b6e9fac26765d65e4b87cc9e60ed2f861e983e0353ace66416a40751252761f2e92c01c645a78896fb138a643c33f2feb5';

  useScript({
    url: `https://app.worknet.ai/widget/${widgetId}.js`,
    id: 'worknet-script-loader',
    type: 'text/javascript',
    unmountCallback: () => {
      document.querySelectorAll('iframe').forEach((iframe) => iframe.remove());
    },
  });
};
